/**
 * Утилита для получения версии файла из метаданных сервера
 * Использует Last-Modified или ETag заголовки для определения версии файла
 */

/**
 * Получает версию файла из заголовков HTTP ответа
 * @param url - URL файла
 * @returns Promise с версией файла (timestamp или ETag)
 */
export const getFileVersion = async (url: string): Promise<string | null> => {
  try {
    const response = await fetch(url, {
      method: 'HEAD', // Используем HEAD запрос для получения только заголовков
      cache: 'no-cache', // Не используем кэш, чтобы получить актуальные заголовки
    });

    if (!response.ok) {
      console.warn('Failed to get file version for:', url, response.status);
      return null;
    }

    // Приоритет 1: ETag (если есть)
    const etag = response.headers.get('ETag');
    if (etag) {
      // Убираем кавычки из ETag если есть
      const cleanEtag = etag.replace(/^["']|["']$/g, '');
      console.log(`✅ Версия файла получена из ETag: ${cleanEtag} (файл: ${url})`);
      return cleanEtag;
    }

    // Приоритет 2: Last-Modified (если есть)
    const lastModified = response.headers.get('Last-Modified');
    if (lastModified) {
      // Преобразуем дату в timestamp
      const timestamp = String(new Date(lastModified).getTime());
      console.log(`✅ Версия файла получена из Last-Modified: ${timestamp} (${lastModified}, файл: ${url})`);
      return timestamp;
    }

    // Приоритет 3: Content-Length + частичный путь (fallback)
    const contentLength = response.headers.get('Content-Length');
    if (contentLength) {
      // Используем комбинацию URL и размера файла как версию
      const urlHash = url.split('/').pop() || '';
      const version = `${urlHash}_${contentLength}`;
      console.log(`⚠️ Версия файла получена из Content-Length: ${version} (файл: ${url})`);
      return version;
    }

    return null;
  } catch (error) {
    console.error('Error getting file version:', error);
    return null;
  }
};

/**
 * Получает версию из нескольких файлов и возвращает самую последнюю
 * @param urls - массив URL файлов
 * @returns Promise с версией (timestamp или hash)
 */
export const getLatestFileVersion = async (urls: string[]): Promise<string | null> => {
  try {
    const versions = await Promise.all(
      urls.map(url => getFileVersion(url))
    );

    // Фильтруем null значения и находим максимальную версию
    const validVersions = versions.filter((v): v is string => v !== null);
    
    if (validVersions.length === 0) {
      return null;
    }

    // Если версии - это timestamps, возвращаем максимальный
    // Если это ETags, возвращаем первый (они уникальны)
    const numericVersions = validVersions
      .map(v => parseInt(v, 10))
      .filter(v => !isNaN(v));

    if (numericVersions.length > 0) {
      return String(Math.max(...numericVersions));
    }

    // Если не numeric, возвращаем первый валидный
    return validVersions[0];
  } catch (error) {
    console.error('Error getting latest file version:', error);
    return null;
  }
};

/**
 * Получает версию из Unity loader.js файла
 * Пытается извлечь версию из содержимого файла, если она там есть
 * @param url - URL loader.js файла
 * @returns Promise с версией или null
 */
export const getUnityLoaderVersion = async (url: string): Promise<string | null> => {
  try {
    console.log(`🔍 Получение версии Unity loader.js из: ${url}`);
    
    // Сначала пытаемся получить версию из заголовков
    const headerVersion = await getFileVersion(url);
    if (headerVersion) {
      console.log(`✅ Версия Unity loader.js получена из заголовков HTTP: ${headerVersion}`);
      return headerVersion;
    }

    console.log(`⚠️ Версия из заголовков не найдена, читаем содержимое файла...`);

    // Если не получилось, пытаемся прочитать содержимое файла
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-cache',
    });

    if (!response.ok) {
      console.error(`❌ Не удалось загрузить loader.js: ${response.status} ${response.statusText}`);
      return null;
    }

    const text = await response.text();
    console.log(`📄 Содержимое loader.js загружено (${text.length} символов)`);
    
    // Ищем версию в содержимом loader.js (может быть в разных форматах)
    // Примеры: "version": "1.0.0", productVersion: "1.0.0", version: "1.0.0"
    const versionMatch = text.match(/(?:productVersion|version|Version)\s*[:=]\s*["']([^"']+)["']/);
    if (versionMatch && versionMatch[1]) {
      const foundVersion = versionMatch[1];
      console.log(`✅ Версия Unity loader.js найдена в содержимом файла: ${foundVersion}`);
      return foundVersion;
    }

    console.log(`⚠️ Версия не найдена в содержимом loader.js, вычисляем хеш содержимого...`);

    // Если версия не найдена, используем хеш содержимого
    const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    const hashArray = Array.from(new Uint8Array(hash));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    
    // Возвращаем первые 16 символов хеша как версию
    const hashVersion = hashHex.substring(0, 16);
    console.log(`✅ Версия Unity loader.js вычислена из хеша содержимого: ${hashVersion} (SHA-256, первые 16 символов)`);
    return hashVersion;
  } catch (error) {
    console.error('Error getting Unity loader version:', error);
    return null;
  }
};

